import {Button, Link, useNavigate} from '@startlibs/components';
import {RadioboxGroup, SimpleRadiobox, TextInput, ToggleCheckbox} from '@startlibs/form'
import {useToggle} from '@startlibs/core'
import React from 'react';
import _ from 'lodash/fp'
import styled from 'styled-components';
import {CASE_CLOSED} from '../../../enums/CaseState'
import {CaseRequestCard} from '../../CaseRequestCard'
import {ConfirmDialog, useConfirmDialog} from '../../../hooks/useConfirmDialog'
import {ContactCheckbox, ContactCheckboxGroup} from '../../../components/ContactCard';
import {LOCAL_PHYSICIAN, REF_PHYSICIAN} from '../../../enums/ContactRelationType'
import {TransitionDiv} from '../../../components/TransitionDiv'
import {buildValidation} from '../../../utils/validation';
import {getContact, PRIMARY} from '../../../enums/ContactRelationKind'
import {getJwt} from '../../../hooks/useJwt'
import {isPaymentPending} from '../../../request/utils'
import {jwtFormFetcher, jwtGetFetcher} from '../../../utils/authFetch'
import {lazyProviderInfo, lazyProviderInfoSec, lazyUserInfo} from '../../../components/WithProvider'
import {useConfirmAnswers} from '../../../expert/customReport/useConfirmAnswers'
import {PendingInfo} from '../info/PendingInfo'
import {useIntl} from 'react-intl'
import { getTimezoneOffset } from '../../../utils/utils';

export const ContactSelectorHeading = styled.p`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

export const useConfirmFinishCase = (caseRequest, setCaseRequest) => {
  const providerInfo = lazyProviderInfo.read()
  const providerInfoSec = lazyProviderInfoSec.read()
  lazyUserInfo.read();
  const isSignatureRequired = providerInfoSec?.reportSignatureRequired || false

  const caseContactsNotifications = providerInfo?.caseContactsNotifications

  
  const referringPhysicianAsSecondary = caseRequest.contactDetails.contactRelationType !== "REF_PHYSICIAN" && caseRequest.referringPhysician.email
  const localPhysicianAsSecondary = caseRequest.contactDetails.contactRelationType !== "LOCAL_PHYSICIAN" && caseRequest.localPhysician.email
  
  const hasNoPaidPayment = !caseRequest.payments.find(payment => payment.paid)
  caseRequest.payments.find(isPaymentPending);

  const navigate = useNavigate()
  const confirmAnswers = useConfirmAnswers(caseRequest)

  const confirmMissingSignatureDialog = useConfirmDialog(
    <ConfirmDialog
      title="Signature required"
      onSuccess={() => navigate(`/admin/request/${caseRequest.requestCode}/review#signature`)}
      confirm={<Button highlight>Edit expert review</Button>}
      closeLabel="Cancel"
    >
      <p>You must sign the review before proceeding.</p>
    </ConfirmDialog>
  )

  const sendMails = useToggle(true)

  const intl = useIntl()

  const primary = getContact(intl, caseRequest, PRIMARY)
  const physician = getContact(intl, caseRequest, REF_PHYSICIAN)
  const localPhysician = getContact(intl, caseRequest, LOCAL_PHYSICIAN)

  React.useEffect(() => {
    if (providerInfo) {
      sendMails.openWith(providerInfo?.notifications?.reportReady && caseContactsNotifications)
    }
  }, [providerInfo])

  const preValidation = ({useCustomReport, ...values}, ...args) => {
    if (isSignatureRequired && !useCustomReport && (!caseRequest.caseInfo.signatures || caseRequest.caseInfo.signatures.length === 0)) {
      confirmMissingSignatureDialog()
      return new Promise(() => {})
    }
    return (useCustomReport ? Promise.resolve() : confirmAnswers()).then(() =>
      buildValidation({'sendToCaseContact': requiredCheck, 'sendToReferringPhysician': requiredCheck, 'sendToLocalPhysician': requiredCheck})(values, ...args)
    )
  }

  const requiredCheck = (v, k, props) => sendMails.isOpen && (referringPhysicianAsSecondary || localPhysicianAsSecondary) && !props.sendToCaseContact && !props.sendToReferringPhysician && !props.sendToLocalPhysician && "No contact was selected."
  

  const openFinishDialog = useConfirmDialog(<ConfirmDialog
    confirmChanges={false}
    isLoading={false}
    notify="Case completed successfully."
    transform={_.update('sendToCaseContact', (v) => v || (sendMails.isOpen && !referringPhysicianAsSecondary))}
    preValidation={preValidation}
    title="Complete report"
    action={jwtFormFetcher(getJwt())("/api/admin/finalizeCase?requestId=" + caseRequest.requestId)}
    values={{useCustomReport: caseRequest.report?.useCustomReport}}
    onSuccess={({useCustomReport}) => {
      // Getting updated case data from database.
      // CaseRequest.report is empty before case is complete.
      // And now caseRequest.report is used in the final report to get persisted expert information
      jwtGetFetcher(getJwt())(`/api/viewCaseRequest?requestCode=${caseRequest.requestCode}&timezoneOffset=${getTimezoneOffset()}`)
        .then((response) => {
          setCaseRequest(response)
        })
        .catch((_error) => {
          setCaseRequest(_.flow(_.set("state", CASE_CLOSED), _.set('report.useCustomReport', useCustomReport)))
        })
        // Fullstory disabled
        // var now = new Date()
        // var eventProperties = {
        //   expertName_str: userInfo.firstName + ' ' + userInfo.lastName,
        //   expertMail_str: userInfo.login,
        //   expertId_int: userInfo.id,
        //   customer_str: providerInfo.name,
        //   dateTime_date: now,
        //   requestId_int: caseRequest.requestId,
        //   caseId_str: caseRequest.requestCode
        // }
        // window.FS.event('Admin Case Finished',eventProperties)
      }
    }
    confirm={<Button highlight>{sendMails.isOpen ? "Complete and notify" : "Proceed"}</Button>}
  >
    {caseContactsNotifications
      ? <p>Complete the report for this case and optionally notify contact(s):</p>
      : <p>Complete the report for this case:</p>
    }
    <CaseRequestCard caseRequest={caseRequest}/>
    {
      caseRequest.report?.customReport && <>
        <p>A custom report was uploaded. Would you like to use it as the final report?</p>
        <RadioboxGroup path="useCustomReport" framedBoxes horizontal wide css="margin-bottom:1.5rem">
          <SimpleRadiobox
            fieldValue={false}
            label="Use default report"
          />
          <SimpleRadiobox
            fieldValue={true}
            label="Use custom report"
          />
        </RadioboxGroup>
      </>}

    {caseContactsNotifications &&
      <ToggleCheckbox
        label="Notify contact(s) that the report is available. "
        descText="This notification can also be sent later from the report screen."
        raw
        value={sendMails.isOpen}
        setValue={_.unary(sendMails.openWith)}
      />
    }

    <TransitionDiv>
      {sendMails.isOpen && <>
        <ContactSelectorHeading>
          {referringPhysicianAsSecondary || localPhysicianAsSecondary ?
            "Select the contact(s) you would like to notify:"
            : "An email will be sent to the following contact:"
          }
          <Link
            className="link"
            to={`/admin/request/${caseRequest.requestCode}/patient`}
            state={{scrollToContact: true}}
          >Edit contacts</Link>
        </ContactSelectorHeading>
        <ContactCheckboxGroup>
          <ContactCheckbox
            path="sendToCaseContact"
            email={primary.email}
            name={primary.name}
            key="patient"
            type={primary.relationType}
          />
          {
            referringPhysicianAsSecondary && <ContactCheckbox
              path="sendToReferringPhysician"
              name={physician.name}
              email={physician.email}
              type="Referring physician"
              key="Referring physician"
            />
          }
          {
            localPhysicianAsSecondary && <ContactCheckbox
              path="sendToLocalPhysician"
              name={localPhysician.name}
              email={localPhysician.email}
              type="Local physician"
              key="Local physician"
            />
          }
        </ContactCheckboxGroup>
        <TextInput
          label="Message"
          path="message"
          textarea
          autoResize
          descText="To be included with the notification."
        />
      </>}
    </TransitionDiv>

    <p>This action will complete the case and move it to &quot;Completed cases&quot;.</p>
    <PendingInfo caseRequest={caseRequest} hideServiceTerms={!providerInfo.serviceTerms || !providerInfo.requiresAgreement}/>
  </ConfirmDialog>, [sendMails.isOpen])

  return () => {
    if (isSignatureRequired && !caseRequest.report?.useCustomReport && (!caseRequest.caseInfo.signatures || caseRequest.caseInfo.signatures.length === 0)) {
      confirmMissingSignatureDialog()
      return
    }
    openFinishDialog()
  }
}
